import React, { useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { toast } from 'sonner';
import Logo from '../components/Logo';
import { makeRESTApiRequest } from '../helpers/api';
import { ENDPOINTS } from '../helpers/constants';
import { ArrowRight, Plus, Trash2 } from 'lucide-react';

const StaffOnboarding = ({ show, onClose }) => {
  const [rows, setRows] = useState([{ email: '', name: '' }]);
  const [submitting, setSubmitting] = useState(false);

  const updateField = (idx, key, value) => {
    const next = [...rows];
    next[idx] = { ...next[idx], [key]: value };
    setRows(next);
  };

  const addRow = () => setRows(prev => [...prev, { email: '', name: '' }]);
  const removeRow = idx => setRows(prev => prev.filter((_, i) => i !== idx));

  const submitInvites = async e => {
    e.preventDefault();
    const toInvite = rows.map(r => ({ email: r.email.trim(), name: r.name.trim() })).filter(r => !!r.email);
    if (toInvite.length === 0) {
      toast.error('Please add at least one email.');
      return;
    }
    setSubmitting(true);
    const { response, error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.INVITE_STAFF,
      method: 'POST',
      requestBody: { staff: toInvite }
    });
    setSubmitting(false);
    if (error) {
      toast.error(error);
      return;
    }
    const invitedCount = response?.invited?.length || 0;
    toast.success(`Invitations sent to ${invitedCount} team member(s).`);
    setRows([{ email: '', name: '' }]);
    onClose();
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered dialogClassName="staff-onboarding-modal" backdrop="static">
      <Modal.Header closeButton={!submitting} className="border-0">
        <div className="w-100 text-center pt-3 pb-1">
          <Logo />
        </div>
      </Modal.Header>
      <Form onSubmit={submitInvites}>
        <Modal.Body className="px-4 pt-0">
          <div className="text-center d-flex flex-column justify-content-center">
            <h1 className="font-weight-bold  xxxlargeFont">Invite your team to your new workspace</h1>
            <p className=" text-secondary w-100 midFont mb-5">
              Workspaces are shared libraries for content. Invite your team and build your workspace together.
            </p>
            {rows.map((row, idx) => (
              <div key={idx} className="w-100 d-flex align-items-center justify-content-between mb-3">
                <Form.Control
                  className="bg-white"
                  type="text"
                  value={row.name}
                  onChange={e => updateField(idx, 'name', e.target.value)}
                  placeholder="Full name"
                  style={{ height: '44px' }}
                  required
                  disabled={submitting}
                />
                <Form.Control
                  className="ml-2 bg-white"
                  type="email"
                  value={row.email}
                  onChange={e => updateField(idx, 'email', e.target.value)}
                  placeholder="name@example.com"
                  style={{ height: '44px' }}
                  required
                  disabled={submitting}
                />

                <Button
                  variant="light"
                  className="align-self-center text-danger border ml-2 px-3"
                  type="button"
                  onClick={() => removeRow(idx)}
                  disabled={rows.length === 1 || submitting}
                  style={{ height: '44px' }}
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            ))}
            <div>
              <Button
                variant="muted"
                type="button"
                className="d-flex align-items-center justify-content-center shadow-none"
                onClick={addRow}
                disabled={submitting}
              >
                <Plus size={16} className="mr-2" /> Add another staff
              </Button>
            </div>
            <div className="d-flex align-items-center justify-content-between mt-5">
              <Button
                variant="link"
                type="button"
                className="ml-2 text-dark"
                onClick={handleClose}
                disabled={submitting}
              >
                Skip for now
              </Button>
              <Button
                type="submit"
                variant="info"
                className="d-inline-flex align-items-center justify-content-center"
                disabled={submitting}
              >
                Invite{' '}
                {submitting ? <Spinner size="sm" animation="border" /> : <ArrowRight size={16} />}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Form>
    </Modal>
  );
};

export default StaffOnboarding;
